/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import ThemeProvider from "@atg/icf-react-components/ThemeProvider";
import ITheme, { PlatformId, ThemeMode } from "@atg/icf-react-components/themes/Theme";
import { FC, Suspense } from "react";
import { createUseStyles } from "react-jss";
import { Outlet } from "react-router-dom";

import AppLoader from "~/components/loader/AppLoader";
import { FontSize, JSS_FONT_FAMILY_PRIMARY, JSS_FONT_FAMILY_SECONDARY } from "~/constants";
import AuthWrapper from "~/features/auth/components/AuthWrapper";
import TwoFactorAuthWrapper from "~/features/auth/components/TwoFactorAuthWrapper";
import Footer from "~/features/footer/components/Footer";
import Header from "~/features/header/components/Header";
import Messages from "~/features/messages/components/Messages";
import ReportTracker from "~/features/reportTracker/components/ReportTracker";
import UnsavedChanges from "~/features/unsavedChanges/components/UnsavedChanges";
import useScrollToTopOnNavigation from "~/hooks/useScrollToTopOnNavigation";

const useStyles = createUseStyles((theme: ITheme) => ({
  "@global": {
    body: {
      margin: 0,
      padding: 0,
      fontSize: FontSize.DEFAULT,
      lineHeight: 1.45,
      color: theme.colors.baseContrast085,
      fontFamily: JSS_FONT_FAMILY_PRIMARY,
    },
    a: {
      fontFamily: JSS_FONT_FAMILY_PRIMARY,
      textDecoration: "none",
      color: theme.colors.primaryDark,
      "&:hover": {
        color: theme.colors.primaryDark,
      },
    },
    "h1, h2, h3, h4, h5, h6": {
      fontFamily: JSS_FONT_FAMILY_SECONDARY,
    },
    h3: {
      fontSize: FontSize.LARGE,
      fontWeight: 400,
      letterSpacing: 1.5,
      textTransform: "uppercase",
      marginTop: 0,
    },
    p: {
      fontFamily: JSS_FONT_FAMILY_PRIMARY,
    },
  },
  appShell: {
    fontFamily: JSS_FONT_FAMILY_PRIMARY,
    position: "relative",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  main: {
    flex: "1 1 100%",
    display: "flex",
    flexDirection: "column",
  },
}));

const AppShell: FC = () => {
  useScrollToTopOnNavigation();

  const classes = useStyles();

  return (
    <AuthWrapper fallback={<AppLoader />}>
      <section className={classes.appShell} data-ta-locator="AppShell-section">
        <Suspense fallback={null}>
          <ThemeProvider platformId={PlatformId.cambuilder} themeMode={ThemeMode.light}>
            <Messages />
            <UnsavedChanges />
            <ReportTracker />
          </ThemeProvider>
        </Suspense>

        <Suspense fallback={null}>
          <ThemeProvider platformId={PlatformId.cambuilder} themeMode={ThemeMode.dark}>
            <Header />
          </ThemeProvider>
        </Suspense>

        <main className={classes.main}>
          <TwoFactorAuthWrapper>
            <Outlet />
          </TwoFactorAuthWrapper>
        </main>

        <Suspense fallback={null}>
          <ThemeProvider platformId={PlatformId.cambuilder} themeMode={ThemeMode.dark}>
            <Footer />
          </ThemeProvider>
        </Suspense>
      </section>
    </AuthWrapper>
  );
};

export default AppShell;
